/**
 * Live-chat side (ett rom "general" MVP).
 *
 * URL: /chat
 * WebSocket: /api/chat/ws?room=general&token=<jwt>
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  ArrowLeft, Send, AlertTriangle, Flag, VolumeX, X, Users,
  Trash2, ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import api, { formatApiError, getStoredToken } from "../lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function fmtTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("nb-NO", { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

/* --------------------------------- Rules modal */
function RulesModal({ open, room, onAccept, onDecline }) {
  const [accepting, setAccepting] = useState(false);
  if (!open) return null;
  const accept = async () => {
    setAccepting(true);
    try { await onAccept(); } finally { setAccepting(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-[var(--noir-elev)] border border-white/10 w-full sm:max-w-lg sm:rounded-lg overflow-y-auto max-h-[95vh]">
        <div className="p-4 border-b border-white/10 flex items-center gap-2">
          <ShieldAlert className="h-5 w-5 text-amber-400" />
          <h3 className="font-fraunces italic text-xl text-[var(--cream)]">Regler for chat</h3>
        </div>
        <div className="p-4 space-y-3 text-sm text-[var(--cream-soft)]">
          <p>Velkommen til <span className="text-[var(--cream)]">{room?.name || "chat"}</span>. Før du deltar må du akseptere disse reglene:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Kun 18+. Ingen mindreårige, ingen henvisninger til mindreårige.</li>
            <li>Ingen deling av personopplysninger (bostedsadresse, telefonnr., osv.).</li>
            <li>Respekter andre. Ingen trakassering, hat eller trusler.</li>
            <li>Ingen spam, reklame eller eksterne lenker uten godkjenning.</li>
            <li>Rapporter regelbrudd — moderatorer varsles umiddelbart.</li>
            <li>Brudd kan gi timeout (15 min–24 t) eller permanent utestengelse.</li>
            <li>Meldinger lagres i {room?.message_ttl_days || 30} dager og slettes automatisk.</li>
          </ul>
        </div>
        <div className="p-4 border-t border-white/10 flex gap-2">
          <button onClick={onDecline} className="btn-ghost flex-1" data-testid="rules-decline">Nei takk</button>
          <button onClick={accept} disabled={accepting} className="btn-magenta flex-1" data-testid="rules-accept">
            {accepting ? "…" : "Aksepter og fortsett"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* --------------------------------- Message row */
function MessageRow({ m, isMe, isMod, onReport, onMute, onDelete, onWhisper }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const isWhisper = m.kind === "whisper";
  return (
    <div className={`group flex items-start gap-2 py-1.5 hover:bg-white/[0.02] px-2 -mx-2 rounded ${isWhisper ? "bg-violet-500/[0.06] border-l-2 border-violet-500/50" : ""}`} data-testid={`chat-msg-${m.id}`}>
      <div className="shrink-0 w-8 h-8 rounded-full overflow-hidden bg-white/5 border border-white/10 flex items-center justify-center text-[10px] text-[var(--cream-soft)]">
        {m.avatar_url ? <img src={m.avatar_url} alt={m.username} className="w-full h-full object-cover"/> : (m.username || "?").slice(0,2).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <Link to={`/profile/${m.username}`} className={`text-sm font-manrope ${isMe ? "text-[var(--magenta)]" : "text-[var(--cream)]"} hover:underline truncate`}>
            {m.username}
          </Link>
          {isWhisper && (
            <span className="text-[10px] text-violet-300 uppercase tracking-[0.1em]">
              🤫 hvisker {isMe ? `til ${m.to_username}` : "til deg"}
            </span>
          )}
          <span className="text-[10px] text-[var(--cream-faint)]">{fmtTime(m.created_at)}</span>
          {m.moderation?.flagged && <span className="text-[9px] text-amber-400 inline-flex items-center gap-0.5"><AlertTriangle className="h-2.5 w-2.5"/> flagg</span>}
        </div>
        <div className="text-sm text-[var(--cream)] break-words whitespace-pre-wrap">{m.body}</div>
      </div>
      {!isMe && (
        <div className="relative shrink-0">
          <button onClick={() => setMenuOpen((x) => !x)}
            className="opacity-0 group-hover:opacity-100 btn-ghost p-1 text-[var(--cream-faint)]" data-testid={`chat-msg-actions-${m.id}`}>
            <span className="text-xs">⋯</span>
          </button>
          {menuOpen && (
            <div className="absolute right-0 mt-1 w-40 bg-[var(--noir-elev)] border border-white/10 shadow-lg z-10 text-xs">
              <button onClick={() => { setMenuOpen(false); onWhisper(m); }} className="w-full text-left px-3 py-2 hover:bg-white/5 text-violet-300 inline-flex items-center gap-1.5">
                <span>🤫</span> Hvisk
              </button>
              <button onClick={() => { setMenuOpen(false); onReport(m); }} className="w-full text-left px-3 py-2 hover:bg-white/5 text-[var(--cream)] inline-flex items-center gap-1.5">
                <Flag className="h-3 w-3"/> Rapporter
              </button>
              <button onClick={() => { setMenuOpen(false); onMute(m); }} className="w-full text-left px-3 py-2 hover:bg-white/5 text-[var(--cream)] inline-flex items-center gap-1.5">
                <VolumeX className="h-3 w-3"/> Mute bruker
              </button>
              {isMod && (
                <button onClick={() => { setMenuOpen(false); onDelete(m); }} className="w-full text-left px-3 py-2 hover:bg-white/5 text-rose-400 inline-flex items-center gap-1.5">
                  <Trash2 className="h-3 w-3"/> Slett (moderator)
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ================================== PAGE */
export default function Chat() {
  const { user, loading } = useAuth();
  const [messages, setMessages] = useState([]);
  const [room, setRoom] = useState(null);
  const [rulesAccepted, setRulesAccepted] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [text, setText] = useState("");
  const [connected, setConnected] = useState(false);
  const [onlineList, setOnlineList] = useState([]);
  const [whisperTarget, setWhisperTarget] = useState(null); // {user_id, username}
  const [typingUsers, setTypingUsers] = useState(new Set());
  const [muted, setMuted] = useState(new Set());
  const [errorMsg, setErrorMsg] = useState(null);
  const wsRef = useRef(null);
  const listRef = useRef(null);
  const typingTimeoutRef = useRef({});
  const reconnectRef = useRef(null);

  const isMod = user?.role === "owner" || user?.role === "admin";

  const scrollBottom = useCallback(() => {
    requestAnimationFrame(() => {
      if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    });
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const r = await api.get("/chat/rooms/general/messages");
      setMessages(r.data.messages || []);
      setRoom(r.data.room || null);
      scrollBottom();
      // Marker som sett så header-badge nullstilles
      api.post("/chat/rooms/general/mark-seen").catch(() => {});
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [scrollBottom]);

  const loadRulesStatus = useCallback(async () => {
    try {
      const r = await api.get("/chat/rooms/general/rules-status");
      setRulesAccepted(!!r.data.accepted);
      if (!r.data.accepted) setShowRules(true);
    } catch { /* ignore */ }
  }, []);

  const loadMutes = useCallback(async () => {
    try {
      const r = await api.get("/chat/mutes");
      setMuted(new Set((r.data.mutes || []).map((m) => m.target_user_id)));
    } catch { /* ignore */ }
  }, []);

  const connectWs = useCallback(() => {
    const token = getStoredToken();
    if (!token) return;
    const wsUrl = BACKEND.replace(/^http/, "ws") + `/api/chat/ws?room=general&token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    ws.onopen = () => setConnected(true);
    ws.onclose = (ev) => {
      setConnected(false);
      if (ev.code === 4401) setErrorMsg("Ikke autentisert.");
      else if (ev.code === 4403) setErrorMsg(ev.reason || "Ingen tilgang.");
      else if (ev.code === 4404) setErrorMsg("Rom finnes ikke.");
      else {
        // Auto-reconnect etter 3s
        if (reconnectRef.current) clearTimeout(reconnectRef.current);
        reconnectRef.current = setTimeout(connectWs, 3000);
      }
    };
    ws.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data);
        if (data.type === "message") {
          setMessages((prev) => [...prev, { ...data.msg, kind: "message" }]);
          scrollBottom();
          // Marker sett fortløpende mens Chat er åpen
          api.post("/chat/rooms/general/mark-seen").catch(() => {});
        } else if (data.type === "whisper") {
          setMessages((prev) => [...prev, {
            id: data.msg.id,
            kind: "whisper",
            user_id: data.msg.from_user_id,
            username: data.msg.from_username,
            avatar_url: data.msg.from_avatar_url,
            body: data.msg.body,
            created_at: data.msg.created_at,
            to_user_id: data.msg.to_user_id,
            to_username: data.msg.to_username,
          }]);
          scrollBottom();
        } else if (data.type === "message.deleted") {
          setMessages((prev) => prev.filter((m) => m.id !== data.id));
        } else if (data.type === "presence") {
          setOnlineList(data.online || []);
        } else if (data.type === "typing") {
          const uid = data.user?.user_id;
          if (!uid) return;
          setTypingUsers((prev) => new Set([...prev, uid]));
          if (typingTimeoutRef.current[uid]) clearTimeout(typingTimeoutRef.current[uid]);
          typingTimeoutRef.current[uid] = setTimeout(() => {
            setTypingUsers((prev) => { const n = new Set(prev); n.delete(uid); return n; });
          }, 3000);
        } else if (data.type === "kicked") {
          toast.error(`Utestengt: ${data.reason || ""}`);
        } else if (data.type === "error") {
          if (data.code === "rate_limited") toast.warning("For raskt — vent litt.");
          else if (data.code === "too_long") toast.warning("Meldingen er for lang.");
          else if (data.code === "rules_not_accepted") setShowRules(true);
          else if (data.code === "banned_word") toast.error(`Forbudt uttrykk — timeout til ${new Date(data.expires_at).toLocaleTimeString("nb-NO")}`);
          else if (data.code === "whisper_target_offline") toast.warning("Brukeren er ikke online lenger.");
          else if (data.code === "whisper_blocked") toast.error("Kan ikke hviske — du er blokkert.");
          else if (data.code === "whisper_muted") toast.error("Kan ikke hviske — bruker har mutet deg.");
          else if (data.code === "whisper_self") toast.warning("Kan ikke hviske til deg selv.");
        }
      } catch { /* ignore */ }
    };
  }, [scrollBottom]);

  useEffect(() => {
    if (!user) return;
    loadRulesStatus();
    loadMutes();
    loadHistory();
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (user && rulesAccepted && !wsRef.current) connectWs();
  }, [user, rulesAccepted, connectWs]);

  const acceptRules = async () => {
    try {
      await api.post("/chat/rooms/general/rules-accept");
      setRulesAccepted(true);
      setShowRules(false);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sendMessage = () => {
    const body = text.trim();
    if (!body) return;
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      toast.error("Ikke tilkoblet");
      return;
    }
    if (whisperTarget) {
      wsRef.current.send(JSON.stringify({ type: "whisper", to_user_id: whisperTarget.user_id, body }));
    } else {
      wsRef.current.send(JSON.stringify({ type: "message", body }));
    }
    setText("");
  };

  const emitTyping = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "typing" }));
    }
  };

  const reportMessage = async (m) => {
    const reason = window.prompt(`Rapporter melding fra ${m.username}. Grunn:`);
    if (!reason || reason.trim().length < 3) return;
    try {
      await api.post(`/chat/messages/${m.id}/report`, { reason: reason.trim() });
      toast.success("Rapportert til moderator.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const muteUser = async (m) => {
    if (!window.confirm(`Mute ${m.username}? Du ser ikke meldingene deres.`)) return;
    try {
      await api.post(`/chat/mute/${m.user_id}`);
      setMuted((prev) => new Set([...prev, m.user_id]));
      setMessages((prev) => prev.filter((x) => x.user_id !== m.user_id));
      toast.success("Muted");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteMessage = async (m) => {
    if (!isMod) return;
    if (!window.confirm(`Slette meldingen? Alle vil se at den er fjernet.`)) return;
    try {
      await api.post(`/chat/messages/${m.id}/delete`, { reason: "moderator" });
      toast.success("Slettet");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const visibleMessages = useMemo(() => messages.filter((m) => !muted.has(m.user_id)), [messages, muted]);
  const typingNames = useMemo(() => {
    if (typingUsers.size === 0) return "";
    const names = messages.filter((m) => typingUsers.has(m.user_id)).slice(-3).map((m) => m.username);
    return names.length ? `${names.join(", ")} skriver…` : `${typingUsers.size} skriver…`;
  }, [typingUsers, messages]);

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-4 flex flex-col" style={{ height: "calc(100vh - 120px)" }} data-testid="chat-page">
        <div className="flex items-center justify-between mb-3 pb-2 border-b border-white/10">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-[var(--cream-faint)] hover:text-[var(--magenta)]"><ArrowLeft className="h-4 w-4" /></Link>
            <div>
              <div className="font-fraunces italic text-xl text-[var(--cream)]">{room?.name || "Chat"}</div>
              <div className="text-[10px] text-[var(--cream-soft)] flex items-center gap-2">
                <span className={`inline-block h-1.5 w-1.5 rounded-full ${connected ? "bg-emerald-400" : "bg-rose-400"}`}/>
                {connected ? `Tilkoblet · ${onlineList.length} online` : "Kobler til…"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 text-[var(--cream-soft)] text-xs">
            <Users className="h-3.5 w-3.5"/> {onlineList.length}
          </div>
        </div>

        {errorMsg && (
          <div className="bg-rose-500/10 border border-rose-500/30 text-rose-300 p-3 mb-3 text-sm flex items-center justify-between">
            <span>{errorMsg}</span>
            <button onClick={() => setErrorMsg(null)} className="btn-ghost p-1"><X className="h-3.5 w-3.5"/></button>
          </div>
        )}

        <div className="flex-1 grid grid-cols-1 md:grid-cols-[1fr_220px] gap-3 min-h-0">
          {/* Meldingsliste */}
          <div className="flex flex-col min-h-0">
            <div ref={listRef} className="flex-1 overflow-y-auto pr-1 border border-white/10 bg-[var(--noir-elev)]/40 p-2 rounded" data-testid="chat-messages">
              {visibleMessages.length === 0 && (
                <div className="text-center py-8 text-[var(--cream-faint)] italic font-fraunces">
                  Ingen meldinger ennå. Start samtalen.
                </div>
              )}
              {visibleMessages.map((m) => (
                <MessageRow key={m.id} m={m}
                  isMe={m.user_id === user.id}
                  isMod={isMod}
                  onReport={reportMessage}
                  onMute={muteUser}
                  onDelete={deleteMessage}
                  onWhisper={(mm) => setWhisperTarget({ user_id: mm.user_id, username: mm.username })}
                />
              ))}
            </div>

            <div className="text-[10px] text-[var(--cream-faint)] italic py-1 h-4">
              {typingNames}
            </div>

            {whisperTarget && (
              <div className="flex items-center justify-between gap-2 bg-violet-500/10 border border-violet-500/30 text-violet-200 px-3 py-1.5 mb-1 text-xs" data-testid="whisper-banner">
                <span>🤫 Hvisker til <b>{whisperTarget.username}</b> — kun dere to ser meldingen.</span>
                <button onClick={() => setWhisperTarget(null)} className="btn-ghost p-1" data-testid="whisper-cancel">
                  <X className="h-3 w-3"/>
                </button>
              </div>
            )}

            <div className="flex items-center gap-2 pt-2 border-t border-white/10" data-testid="chat-input-bar">
              <input
                type="text"
                value={text}
                onChange={(e) => { setText(e.target.value); emitTyping(); }}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                placeholder={rulesAccepted ? (whisperTarget ? `Hvisk til ${whisperTarget.username}…` : "Skriv en melding…") : "Aksepter reglene for å skrive"}
                maxLength={800}
                disabled={!rulesAccepted || !connected}
                className="input-editorial flex-1"
                data-testid="chat-input"
              />
              <button onClick={sendMessage} disabled={!text.trim() || !connected}
                className={`inline-flex items-center gap-1.5 ${whisperTarget ? "btn-ghost border-violet-500/50 text-violet-300" : "btn-magenta"}`}
                data-testid="chat-send">
                <Send className="h-3.5 w-3.5"/> {whisperTarget ? "Hvisk" : "Send"}
              </button>
            </div>
          </div>

          {/* Online-liste */}
          <aside className="hidden md:flex flex-col border border-white/10 bg-[var(--noir-elev)]/40 p-3 rounded" data-testid="chat-online-list">
            <div className="font-fraunces italic text-sm text-[var(--cream)] mb-2 flex items-center gap-1.5">
              <Users className="h-3.5 w-3.5"/> Innlogget ({onlineList.length})
            </div>
            <div className="flex-1 overflow-y-auto space-y-1">
              {onlineList.length === 0 && <div className="text-xs text-[var(--cream-faint)] italic">Ingen online</div>}
              {onlineList.map((u) => (
                <div key={u.user_id}
                  className={`flex items-center gap-2 p-1.5 rounded hover:bg-white/5 group ${u.user_id === user.id ? "bg-white/[0.03]" : ""}`}
                  data-testid={`online-user-${u.user_id}`}>
                  <div className="shrink-0 w-6 h-6 rounded-full overflow-hidden bg-white/5 border border-white/10 flex items-center justify-center text-[9px] text-[var(--cream-soft)] relative">
                    {u.avatar_url ? <img src={u.avatar_url} alt={u.username} className="w-full h-full object-cover"/> : (u.username || "?").slice(0,2).toUpperCase()}
                    <span className="absolute -bottom-0.5 -right-0.5 h-1.5 w-1.5 rounded-full bg-emerald-400 border border-[var(--noir-elev)]"/>
                  </div>
                  <Link to={`/profile/${u.username}`} className="flex-1 text-xs text-[var(--cream)] hover:text-[var(--magenta)] truncate">
                    {u.username}{u.user_id === user.id && <span className="text-[var(--cream-faint)]"> (deg)</span>}
                  </Link>
                  {u.user_id !== user.id && (
                    <button
                      onClick={() => setWhisperTarget({ user_id: u.user_id, username: u.username })}
                      className="opacity-0 group-hover:opacity-100 btn-ghost p-1 text-violet-300"
                      title="Hvisk"
                      data-testid={`whisper-btn-${u.user_id}`}>
                      <span className="text-xs">🤫</span>
                    </button>
                  )}
                </div>
              ))}
            </div>
          </aside>
        </div>
      </div>

      <RulesModal
        open={showRules}
        room={room}
        onAccept={acceptRules}
        onDecline={() => { setShowRules(false); toast.info("Du må akseptere reglene for å skrive."); }}
      />
    </Layout>
  );
}
